/**
 * (Группа 1 · экран 1) Вход оператора платформы.
 *
 * Поток: login → (если totp_required) /2fa → дашборд. Если бэкенд вернул
 * полноценный access_token без второго фактора — сразу авторизуем и уводим
 * на дашборд.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Button, Input } from '@hsc/ui';
import { useAuth } from '@hsc/auth';
import { login } from '../../lib/api.js';
import { writeAccessToken, writeRefreshToken } from '../../lib/storage.js';
import { stashMfaToken } from './mfaSession.js';

const schema = z.object({
  email: z.string().email('Введите корректный email'),
  password: z.string().min(8, 'Минимум 8 символов'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const auth = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const res = await login({ email: values.email, password: values.password });
      // Бэкенд требует второй фактор → переходим на экран TOTP.
      if (res.totp_required) {
        stashMfaToken(res.mfa_token ?? '');
        navigate('/2fa', { replace: true });
        return;
      }
      writeAccessToken(res.access_token);
      if (res.refresh_token) writeRefreshToken(res.refresh_token);
      auth.login(res.access_token);
      navigate('/dashboard', { replace: true });
    } catch (e) {
      const err = e as { status?: number };
      if (err.status === 401) {
        setSubmitError('Неверный email или пароль');
      } else if (err.status === 429) {
        setSubmitError('Слишком много попыток. Аккаунт временно заблокирован.');
      } else {
        setSubmitError('Не удалось войти. Попробуйте позже.');
      }
    }
  });

  return (
    <main className="hsc-login">
      <div className="hsc-login__box" data-testid="login-card">
        <div className="hsc-login__brand">
          <span aria-hidden="true" className="hsc-login__logo">L</span>
          <h1 className="hsc-login__title">LovArt Hotels</h1>
          <p className="hsc-login__subtitle">Консоль администратора платформы</p>
        </div>
        <form onSubmit={onSubmit} noValidate aria-describedby={submitError ? 'login-error' : undefined}>
          <div className="hsc-stack">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="admin@lovarthotels.com"
              {...register('email')}
              {...(errors.email ? { error: errors.email.message } : {})}
            />
            <Input
              label="Пароль"
              type="password"
              autoComplete="current-password"
              placeholder="Минимум 8 символов"
              {...register('password')}
              {...(errors.password ? { error: errors.password.message } : {})}
            />
            {submitError && (
              <div id="login-error" role="alert" className="hsc-input__error">
                {submitError}
              </div>
            )}
            <Button type="submit" variant="primary" size="lg" loading={isSubmitting}>
              Войти
            </Button>
            <Link to="/recovery" className="admin-link">
              Забыли пароль?
            </Link>
          </div>
        </form>
      </div>
    </main>
  );
}
